import React from "react";
import {
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import FlashOnOutlinedIcon from "@mui/icons-material/FlashOnOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import styles from "./phishingDetail.module.scss";
import {
  phishingHero,
  phishingEvents,
  affectedAssets,
  deepDiveLogs,
} from "./phishingData";
import ComplianceBreadcrumb from "../compliance/ComplianceBreadcrumb";
import { downloadPDF } from "../hipaa/HipaaDetail";
import DnsOutlinedIcon from "@mui/icons-material/DnsOutlined";
import { aiStrategy } from "../hipaa/hipaaData";
const Gauge = ({ value }) => {
  const dashArray = 283;
  const dashOffset = dashArray - (dashArray * value) / 100;
  return (
    <div className={styles.gauge}>
      <svg viewBox="0 0 100 100">
        <circle className={styles.gaugeBg} cx="50" cy="50" r="45" />
        <circle
          className={styles.gaugeFill}
          cx="50"
          cy="50"
          r="45"
          strokeDasharray={dashArray}
          strokeDashoffset={dashOffset}
        />
      </svg>
      <div className={styles.gaugeValue}>{value}%</div>
    </div>
  );
};

const EventItem = ({ time, text, tone }) => {
  const iconMap = {
    // info: <InfoOutlinedIcon fontSize="small" />,
    warning: <WarningAmberOutlinedIcon fontSize="small" />,
    alert: <ErrorOutlineOutlinedIcon fontSize="small" />,
    success: <CheckCircleOutlineOutlinedIcon fontSize="small" />,
  };

  return (
    <div className={styles.eventItem}>
      <div className={`${styles.eventIcon} ${styles[`${tone}Bg`]}`}>
        {iconMap[tone] || <AccessTimeOutlinedIcon fontSize="small" />}
      </div>
      <div className={styles.eventContent}>
        <div className={styles.eventMeta}>
          <span className={styles.eventTime}>{time}</span>
          <span className={`${styles.eventStatus} ${styles[tone]}`}>
            {tone}
          </span>
        </div>
        <div className={styles.eventText}>{text}</div>
      </div>
    </div>
  );
};

const AssetCard = ({ asset }) => (
  <Card className={styles.assetCard} variant="outlined">
    <Stack direction={"row"} gap={2}>
      <Avatar variant="rounded">
        <DnsOutlinedIcon />
      </Avatar>
      <div className={styles.assetBody}>
        <div className={styles.assetName}>{asset.name}</div>
        <div className={styles.assetDesc}>{asset.desc}</div>
      </div>
    </Stack>
    <div className={styles.assetMeta}>
      <div>
        <div className={styles.metaLabel}>Status</div>
        <div className={styles.metaLabel}>Risk</div>
      </div>
      <div>
        <div className={styles.metaValue}>{asset.status}</div>
        <span className={`${styles.riskPill} ${styles[asset.riskTone]}`}>
          {asset.risk}
        </span>
      </div>
    </div>
  </Card>
);

const PhishingDetail = () => {
  return (
    <Box className={styles.page}>
      <div className={styles.topBar}>
        <ComplianceBreadcrumb
          items={[
            { label: "CISO Dashboard", href: "/" },
            { label: "Phishing events" },
          ]}
        />{" "}
        <Button
          variant="contained"
          startIcon={<DownloadOutlinedIcon />}
          className={styles.exportButton}
          onClick={() => downloadPDF("Phishing")}
        >
          Export Report
        </Button>
      </div>
      <Stack id="report" direction={"column"} gap={2}>
        <TopCard
          title={phishingHero.title}
          score={phishingHero.score}
          description={phishingHero.description}
          severity={phishingHero.severity}
        />
        <Card
          className={styles.aiCard}
          sx={{ display: "flex", alignItems: "center" }}
        >
          <Box>
            <Avatar variant="rounded" sx={{ bgcolor: "blue" }}>
              <FlashOnOutlinedIcon />
            </Avatar>
          </Box>
          <Box>
            <Typography fontWeight={700}>AI Remediation Strategy</Typography>
            <div className={styles.aiText}>{aiStrategy}</div>
          </Box>
        </Card>
        <Card className={styles.eventsCard}>
          <Typography className={styles.sectionTitle} fontWeight={700}>
            Phishing Events
          </Typography>
          <div className={styles.eventList}>
            {phishingEvents.map((evt, idx) => (
              <EventItem key={`${evt.time}-${idx}`} {...evt} />
            ))}
          </div>
        </Card>
        <Card className={styles.assetsCard}>
          <div className={styles.assetsHeader}>
            <Typography className={styles.sectionTitle} fontWeight={700}>
              Affected Assets Panel
            </Typography>
            <div className={styles.assetActions}>
              <Button
                variant="outlined"
                size="small"
                className={styles.outlineButton}
              >
                Export Forensic Evidence
              </Button>
              <Button
                variant="contained"
                size="small"
                className={styles.isolateButton}
              >
                Isolate Affected Network Zones
              </Button>
            </div>
          </div>
          <div className={styles.assetGrid}>
            {affectedAssets.map((asset) => (
              <AssetCard key={`${asset.name}-${asset.status}`} asset={asset} />
            ))}
          </div>
        </Card>
        <Card className={styles.tableCard}>
          <Typography className={styles.sectionTitle} fontWeight={700}>
            Deep Dive Logs
          </Typography>
          <TableContainer className={styles.tableContainer}>
            <Table size="medium">
              <TableHead>
                <TableRow>
                  <TableCell className={styles.tableHead}>Asset Name</TableCell>
                  <TableCell className={styles.tableHead}>Source IP</TableCell>
                  <TableCell className={styles.tableHead}>
                    Destination IP
                  </TableCell>
                  <TableCell className={styles.tableHead}>Protocol</TableCell>
                  <TableCell className={styles.tableHead}>Bytes Sent</TableCell>
                  <TableCell className={styles.tableHead}>Flag</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {deepDiveLogs.map((row, idx) => (
                  <TableRow key={`${row.asset}-${idx}`}>
                    <TableCell>
                      <Stack direction={"row"} gap={1} alignItems="center">
                        <Avatar variant="rounded">
                          <DnsOutlinedIcon />
                        </Avatar>
                        {row.asset}
                      </Stack>
                    </TableCell>
                    <TableCell>{row.src}</TableCell>
                    <TableCell>{row.dest}</TableCell>
                    <TableCell>{row.proto}</TableCell>
                    <TableCell>{row.bytes}</TableCell>
                    <TableCell>
                      <span className={styles.flagPill}>{row.flag}</span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      </Stack>
    </Box>
  );
};

export default PhishingDetail;
export const TopCard = ({ title, description, severity, score }) => {
  return (
    <Card className={styles.heroCard}>
      <div>
        <Chip label={severity} size="small" className={styles.severityChip} />
        <div className={styles.heroTitle}>{title}</div>
        <div className={styles.heroDesc}>{description}</div>
      </div>
      <Gauge value={score} />
    </Card>
  );
};
